import React, { useState } from 'react';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { servicesAPI } from '../services/api';
import { getUser } from '../services/auth';
import { resolveApiErrorMessage } from '../utils/apiErrors';
import { MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useLocalization } from '../context/LocalizationContext';

const buildServiceReference = (value = '') => {
  const match = String(value || '').match(/#\s*(\d+)\s*$/);
  if (!match) return '';
  return `REF-${match[1]}`;
};

const formatServiceSuccessMessage = (payload, t) => {
  const rawMessage = String(payload?.value || payload?.message || '').trim();
  const referenceNo = buildServiceReference(rawMessage);

  if (!referenceNo) {
    return rawMessage || t('services.submitRequest');
  }

  return t('services.referenceSuccess', { referenceNo });
};

export const Services = () => {
  const { needsCompactFont, t } = useLocalization();
  const user = getUser();
  const fieldLabelClass = 'app-form-label';
  const fieldControlClass = 'app-form-control';
  const [formData, setFormData] = useState({
    category: 'general_query',
    contactEmail: '',
    serviceRequest: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.serviceRequest.trim().length < 20) {
      toast.error(t('services.requestTooShort'));
      return;
    }

    setLoading(true);

    try {
      const categoryLabel = formData.category.replace(/_/g, ' ').toUpperCase();
      const payload = {
        customerId: user?.customerId || '',
        customerName: user?.name || user?.customerId ||'',
        mobile: user?.mobileNumber || '',
        email: formData.contactEmail || null,
        srType: `[${categoryLabel}] ${formData.serviceRequest.trim()}`,
        sourceUrl: window.location.origin,
        timestamp: new Date().toISOString(),
      };
      
      const response = await servicesAPI.createRequest(payload);
      const message = formatServiceSuccessMessage(response.data, t);
      toast.success(message);
      setFormData((prev) => ({ ...prev, serviceRequest: '' }));
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, t, t('services.failedToSubmit')));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="app-page-shell max-w-5xl fade-in" data-testid="services-page">
        <div className="pointer-events-none absolute -z-10 inset-0 dark:hidden">
          <div className="absolute -top-8 -left-8 h-40 w-40 rounded-full bg-primary-200/40 blur-3xl" />
          <div className="absolute top-24 right-0 h-44 w-44 rounded-full bg-primary-200/35 blur-3xl" />
        </div>
        <div>
          <h1
            className={`app-page-heading ${needsCompactFont ? 'app-page-heading-te' : ''}`}
          >
            {t('services.title')}
          </h1>
          <p className="app-page-subtitle">{t('services.subtitle')}</p>
        </div>

        <div className="app-surface app-surface-section">
          <div className="flex items-center space-x-3 mb-5">
            <div className="app-icon-chip rounded-full">
              <MessageSquare className="w-6 h-6 text-primary-500" strokeWidth={1.5} />
            </div>
            <h2 className="app-panel-title">{t('services.newRequest')}</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="service-request-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="requestCategory" className={fieldLabelClass}>
                  {t('services.category')}
                </label>
                <select
                  id="requestCategory"
                  data-testid="service-request-category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className={fieldControlClass}
                >
                  <option value="general_query">{t('services.generalQuery')}</option>
                  <option value="complaint">{t('services.complaint')}</option>
                  <option value="account_support">{t('services.accountSupport')}</option>
                  <option value="loan_support">{t('services.loanSupport')}</option>
                </select>
              </div>
              <div>
                <label htmlFor="contactEmail" className={fieldLabelClass}>
                  {t('services.contactEmail')}
                </label>
                <input
                  id="contactEmail"
                  data-testid="service-request-email"
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  className={fieldControlClass}
                  placeholder="name@example.com"
                />
                <p className="mt-2 text-xs sm:text-sm text-primary-600 dark:text-primary-300">
                  {t('services.emailHelp')}
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="serviceRequest" className={fieldLabelClass}>
                {t('services.queryOrComplaint')}
              </label>
              <textarea
                id="serviceRequest"
                data-testid="service-request-textarea"
                required
                maxLength={800}
                value={formData.serviceRequest}
                onChange={(e) => setFormData({ ...formData, serviceRequest: e.target.value })}
                rows={8}
                className={`${fieldControlClass} resize-none`}
                placeholder={t('services.queryOrComplaint')}
              />
              <div className="mt-3 rounded-xl border border-primary-200 dark:border-slate-700 bg-primary-50 dark:bg-slate-800/70 px-3 py-2.5 flex items-start justify-between gap-3">
                <p className="text-xs sm:text-sm text-primary-800 dark:text-white leading-relaxed">
                  {t('services.descriptionHint')}
                </p>
                <span className="text-xs sm:text-sm font-semibold text-primary-800 dark:text-white whitespace-nowrap">
                  {formData.serviceRequest.length}/800
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-request-button"
              className="app-primary-button w-full"
            >
              <Send size={18} />
              <span>{loading ? t('services.submitting') : t('services.submitRequest')}</span>
            </button>

            <div className="app-note">
              <p className="text-sm text-primary-900 dark:text-white text-center">
                {t('services.submittedNote')}
              </p>
            </div>
          </form>
        </div>

        <div className="app-surface app-surface-section">
          <h3 className="app-panel-title mb-4">{t('services.needAssistance')}</h3>
          <p className="app-panel-subtitle mb-4">{t('services.contactSupport')}</p>
          <ul className="space-y-3 text-primary-800 dark:text-primary-200">
            <li className="flex items-start gap-3">
              <span className="text-primary-500 font-bold mt-1">•</span>
              <span><span className="font-medium">{t('services.email')}:</span> smartcbs@ykinnosoft.com</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary-500 font-bold mt-1">•</span>
              <span><span className="font-medium">{t('services.hours')}:</span> Mon-Sat, 9:00 AM - 6:00 PM</span>
            </li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
};
